'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { checkDeviceType } from '@benzinga/device-utils';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { Icon } from '@benzinga/core-ui';
import Hooks from '@benzinga/hooks';

declare global {
  interface Navigator {
    getInstalledRelatedApps?: () => Promise<RelatedApplication[]>;
  }
}

interface RelatedApplication {
  id: string;
  platform: string;
  url?: string;
  version?: string;
}

const AppleAppStoreIcon = (
  <svg fill="none" height="16" viewBox="0 0 13 16" width="13" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.421 8.549c-.019-1.938 1.586-2.88 1.66-2.925-.909-1.325-2.317-1.505-2.812-1.52-1.182-.123-2.329.71-2.93.71-.615 0-1.544-.697-2.542-.676-1.288.02-2.491.766-3.151 1.921-1.36 2.359-.346 5.824.96 7.73.653.933 1.415 1.977 2.413 1.939.977-.04 1.342-.622 2.52-.622 1.169 0 1.512.622 2.53.599 1.048-.018 1.708-.938 2.337-1.88.756-1.07 1.058-2.124 1.07-2.178-.025-.009-2.035-.776-2.055-3.098zM8.498 2.73c.525-.656.887-1.552.785-2.459-.76.035-1.71.527-2.258 1.169-.484.567-.919 1.495-.806 2.367.855.064 1.731-.43 2.28-1.076z"
      fill="#fff"
    ></path>
  </svg>
);

const GoogleAppStoreIcon = (
  <svg height="800" viewBox="0 -0.5 408 467.80000000000007" width="728" xmlns="http://www.w3.org/2000/svg">
    <linearGradient gradientUnits="userSpaceOnUse" id="a" x2="261.746" y1="112.094" y2="112.094">
      <stop offset="0" stopColor="#63be6b" />
      <stop offset=".506" stopColor="#5bbc6a" />
      <stop offset="1" stopColor="#4ab96a" />
    </linearGradient>
    <linearGradient gradientUnits="userSpaceOnUse" id="b" x1=".152" x2="179.896" y1="223.393" y2="223.393">
      <stop offset="0" stopColor="#3ec6f2" />
      <stop offset="1" stopColor="#45afe3" />
    </linearGradient>
    <linearGradient gradientUnits="userSpaceOnUse" id="c" x1="179.896" x2="407.976" y1="229.464" y2="229.464">
      <stop offset="0" stopColor="#faa51a" />
      <stop offset=".387" stopColor="#fab716" />
      <stop offset=".741" stopColor="#fac412" />
      <stop offset="1" stopColor="#fac80f" />
    </linearGradient>
    <linearGradient gradientUnits="userSpaceOnUse" id="d" x1="1.744" x2="272.296" y1="345.521" y2="345.521">
      <stop offset="0" stopColor="#ec3b50" />
      <stop offset="1" stopColor="#e7515b" />
    </linearGradient>
    <path d="M261.7 142.3L15 1.3C11.9-.5 8-.4 5 1.4c-3.1 1.8-5 5-5 8.6 0 0 .1 13 .2 34.4l179.7 179.7z" fill="url(#a)" />
    <path d="M.2 44.4C.5 121.6 1.4 309 1.8 402.3L180 224.1z" fill="url(#b)" />
    <path
      d="M402.9 223l-141.2-80.7-81.9 81.8 92.4 92.4L403 240.3c3.1-1.8 5-5.1 5-8.6 0-3.6-2-6.9-5.1-8.7z"
      fill="url(#c)"
    />
    <path
      d="M1.7 402.3c.2 33.3.3 54.6.3 54.6 0 3.6 1.9 6.9 5 8.6 3.1 1.8 6.9 1.8 10 0l255.3-148.9-92.4-92.4z"
      fill="url(#d)"
    />
  </svg>
);

export const MobileAppBannerAd: React.FC = () => {
  const [url, setUrl] = React.useState<string>('');
  const [isInstalled, setIsInstalled] = React.useState(false);
  const [isMobileAppBannerAdShown, setIsMobileAppBannerAdShown] = Hooks.useLocalStorage(
    'show_mobile_app_banner_ad',
    true,
  );

  React.useEffect(() => {
    const checkIsAppInstalled = async (): Promise<boolean> => {
      if ('getInstalledRelatedApps' in navigator && typeof navigator.getInstalledRelatedApps !== 'undefined') {
        const relatedApps = await navigator.getInstalledRelatedApps();
        return relatedApps.some(app => app.id === 'com.benzinga.app');
      }
      return false;
    };

    checkIsAppInstalled()
      .then(_isInstalled => {
        if (_isInstalled) {
          setIsInstalled(_isInstalled);
          return;
        }
        handleSetUrl();
      })
      .catch(() => {
        handleSetUrl();
      });

    const handleSetUrl = () => {
      const device = checkDeviceType();
      const isIos = device.isIos();
      if (isIos) {
        setUrl('https://apps.apple.com/us/app/benzinga-financial-news-data/id688949481');
      } else {
        setUrl('https://play.google.com/store/apps/details?id=com.benzinga.app&hl=en_NZ&gl=US');
      }
    };
  }, []);

  if (isInstalled) return null;
  if (!isMobileAppBannerAdShown) return null;

  const handleCloseBanner = () => {
    setIsMobileAppBannerAdShown(false);
  };

  return (
    <Container>
      {url && (
        <a href={url} rel="noreferrer" target="_blank">
          <SlantedWrapper>
            <PhoneImage />
            <SlantedDiv />
            <SlantedBorder />
          </SlantedWrapper>
          <div className="mx-4">Try Benzinga's Mobile App</div>
          <div className="app-store-wrapper">
            <div className="apple-app">{AppleAppStoreIcon}</div>
            <div className="google-app">{GoogleAppStoreIcon}</div>
          </div>
        </a>
      )}
      <button
        className="flex items-center justify-center h-full w-10 absolute right-0 text-white"
        onClick={handleCloseBanner}
      >
        <Icon icon={faClose} />
      </button>
    </Container>
  );
};

export default MobileAppBannerAd;

const Container = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #3f83f8;
  //padding: 12px;
  z-index: 10;
  height: 45px;
  a {
    display: flex;
    //justify-content: center;
    align-items: center;
    color: white;
    text-decoration: none;
    font-weight: 700;
    font-size: 1rem;
    width: 100%;
    /* div {
      margin-right: 10px;
    } */
  }

  .app-store-wrapper {
    gap: 0.5rem;
    display: none;

    @media (min-width: 390px) {
      display: flex;
    }

    svg {
      width: 1.25rem;
      height: 1.25rem;
    }
  }

  @media (min-width: 800px) {
    display: none;
  }
`;

const PhoneImage = styled.div`
  background-image: url('/next-assets/images/benzinga-mobile-app-preview.png');
  background-repeat: no-repeat;
  background-position-y: bottom;
  background-position-x: 10px;
  background-size: 48px;
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
`;

const SlantedWrapper = styled.div`
  position: relative;
  left: 0;
  width: 70px;
  height: 45px;
`;

const SlantedDiv = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: #479cff;
  transform: skew(-14deg);
  transform-origin: top left;
`;

const SlantedBorder = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 2px; // Adjust this value to change border thickness
  height: 120%; // Extend beyond the div to account for skew
  background-color: #55a3ff; // Change this to your desired border color
  transform: skew(-14deg);
  transform-origin: top right;
`;
